/* eslint-disable import/named */

import * as yup from 'yup/lib';
import locale from 'yup/lib/locale';

import { PLEASE_ENTER_VIDEO_HOST_LINK } from 'constants/texts';
import { isNil } from 'ramda';
import { isValidCoords } from 'utils/maps';

import { dateFormat } from './form/masks';
import { isValidVideoUrl } from './parseVideoUrl';

export const ERROR_MESSAGES = {
  required: 'This is a required field',
  email: 'Email address is invalid',
  url: 'Please enter a valid url',
  videoURL: PLEASE_ENTER_VIDEO_HOST_LINK,
  hex: 'Please use HEX format #000000',
  passwordConfirmed: 'Please make sure your passwords match',
  passwordLength: 'Your password must be at least 8 characters.',
  agreed: 'Please confirm to continue',
  checked: 'Please check box to continue.',
  country: 'You must select a country',
  tags: 'Please enter at least one tag',
  accountTypes: 'Please choose at least one account type',
  coords: 'Invalid coordinates. Please try again.',
  date: `Please enter a date in the format ${dateFormat}`,
  message: 'You must add a message',
  requirements: 'Password does not match the requirements',
};

yup.setLocale({
  mixed: {
    required: ERROR_MESSAGES.required,
  },
  string: {
    email: ERROR_MESSAGES.email,
    url: ERROR_MESSAGES.url,
    videoURL: ERROR_MESSAGES.videoURL,
    hex: ERROR_MESSAGES.hex,
    passwordConfirmed: ERROR_MESSAGES.passwordConfirmed,
    passwordLength: ERROR_MESSAGES.passwordLength,
    coords: ERROR_MESSAGES.coords,
    requirements: ERROR_MESSAGES.requirements,
  },
  boolean: {
    agreed: ERROR_MESSAGES.agreed,
    checked: ERROR_MESSAGES.checked,
  },
});

const rHex = /(^#[a-fA-F0-9]{6}|^#[a-fA-F0-9]{3})$/;

/**
 * PASSWORD CONFIRMATION
 */
yup.addMethod(yup.string, 'passwordConfirmed', function (msg, ref = yup.ref('password')) {
  if (msg === void 0) {
    msg = locale.string.passwordConfirmed;
  }

  return this.test({
    message: msg,
    name: 'passwordConfirmed',
    test(value) {
      return this.resolve(ref) === value;
    },
  });
});

/**
 * REQUIREMENTS
 */
yup.addMethod(yup.string, 'requirements', function (msg) {
  if (msg === void 0) {
    msg = locale.string.requirements;
  }

  return this.test({
    message: msg,
    name: 'requirements',
    test(value) {
      const regexp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9])(?=.{8,})/;

      return regexp.test(value);
    },
  });
});

/**
 * PASSWORD LENGTH
 */
yup.addMethod(yup.string, 'passwordLength', function (msg) {
  if (msg === void 0) {
    msg = locale.string.passwordLength;
  }

  return this.test({
    message: msg,
    name: 'passwordLength',
    test(value) {
      return !value || value.length >= 8;
    },
  });
});

/**
 * HEX COLOR
 */
yup.addMethod(yup.string, 'hex', function (msg) {
  if (msg === void 0) {
    msg = locale.string.hex;
  }

  return this.matches(rHex, {
    name: 'hex',
    message: msg,
    excludeEmptyString: true,
  });
});

/**
 * AGREEMENT
 */
yup.addMethod(yup.bool, 'agreed', function (msg) {
  if (msg === void 0) {
    msg = locale.boolean.agreed;
  }

  return this.transform(function (value) {
    if (!this.isType(value)) {
      if (/^(true|1)$/i.test(value)) return true;
      if (/^(false|0)$/i.test(value)) return false;
    }

    return value;
  }).test({
    message: msg,
    name: 'agreed',
    test: (value) => value === true,
  });
});

yup.addMethod(yup.bool, 'checked', function (msg) {
  if (msg === void 0) {
    msg = locale.boolean.checked;
  }

  return this.transform(function (value) {
    if (!this.isType(value)) {
      if (/^(true|1)$/i.test(value)) return true;
      if (/^(false|0|null)$/i.test(value)) return false;
    }

    return value;
  }).test({
    message: msg,
    name: 'checked',
    test: (value) => value === true,
  });
});

yup.addMethod(yup.string, 'videoURL', function (msg) {
  if (msg === void 0) {
    msg = locale.string.videoURL;
  }

  return this.test({
    message: msg,
    name: 'videoURL',
    test(value) {
      if (isNil(value)) return true;
      return isValidVideoUrl(value);
    },
  });
});

yup.addMethod(yup.string, 'coords', function (msg) {
  if (msg === void 0) {
    msg = locale.string.coords;
  }

  return this.test({
    message: msg,
    name: 'coords',
    test(value) {
      return isValidCoords(value);
    },
  });
});

const socialLinksConfig = {
  facebook: {
    regexp:
      /^(?:https?:\/\/)?(?:www\.)?(?:facebook|fb|m\.facebook)\.(?:com|me)\/[\p{L}\p{N}\-._~!$&'()*+,;=:@/?%#[\]=]+$/u,
    name: 'Facebook',
  },
  vimeo: {
    regexp: /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/[\p{L}\p{N}\-._~!$&'()*+,;=:@/?%#[\]=]+$/u,
    name: 'Vimeo',
  },
  instagram: {
    regexp: /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/[\p{L}\p{N}\-._~!$&'()*+,;=:@/?%#[\]=]+$/u,
    name: 'Instagram',
  },
};

yup.addMethod(yup.string, 'socialLink', function (msg) {
  return this.test({
    message: `Please enter a valid ${socialLinksConfig[msg].name} link`,
    name: 'socialLink',
    test(value) {
      return isNil(value) || socialLinksConfig[msg].regexp.test(value);
    },
  });
});

yup.addMethod(yup.string, 'domainURL', function () {
  return this.test({
    message: 'Please enter a valid url with letters, digits and dash symbol. Do not use protocol (e.g. https://)',
    name: 'domainURL',
    test(value) {
      const regexp =
        /^(?!:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?!:[/?#]\S*)?$/i;

      return regexp.test(value);
    },
  });
});

yup.addMethod(yup.string, 'websiteURL', function (message = 'Invalid URL', nullable = true) {
  return this.test({
    name: 'websiteURL',
    message,
    test(value) {
      if (nullable && (value === '' || isNil(value))) return true;

      const regexp = /^(?:https?:\/\/)?(?:www\.)?[\w-]+(?:\.[\w-]+)+[\w.,@?^=%&:/~+#-]*$/;
      return regexp.test(value);
    },
  });
});

export default yup;
